import { useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Globe,
    TrendingUp,
    Target,
    ArrowLeftRight,
    ShieldCheck,
    Scale,
    OctagonX,
    ClipboardCheck,
    Printer,
    RotateCcw,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Trading Plan Data Structure
const tradingPlanSections = {
    market_session: {
        title: 'Market & Session',
        title_id: 'Market & Sesi',
        icon: Globe,
        color: 'blue',
        items: [
            { id: 'ms_1', text: 'Instrument:', text_id: 'Instrument:', hasInput: true, placeholder: 'e.g. XAUUSD, EURUSD' },
            { id: 'ms_2', text: 'Trading Session:', text_id: 'Sesi Trading:', hasSelect: true, options: ['London', 'New York', 'London + New York', 'Asia'] }
        ]
    },
    market_direction: {
        title: 'Market Direction',
        title_id: 'Arah Market',
        icon: TrendingUp,
        color: 'emerald',
        items: [
            { id: 'md_1', text: 'Trade only with main trend', text_id: 'Trade hanya ikut trend utama', isRule: true },
            { id: 'md_2', text: 'Avoid unclear market conditions', text_id: 'Elak market yang tidak jelas', isRule: true }
        ]
    },
    setup: {
        title: 'Setup',
        title_id: 'Setup',
        icon: Target,
        color: 'purple',
        items: [
            { id: 'st_1', text: 'Entry only at planned zone', text_id: 'Entry hanya di zone yang dirancang', isRule: true },
            { id: 'st_2', text: 'Confirmation required', text_id: 'Mesti ada confirmation', isRule: true },
            { id: 'st_3', text: 'No setup = No trade', text_id: 'Tiada setup = Tiada trade', isRule: true, highlight: true }
        ]
    },
    entry_exit: {
        title: 'Entry & Exit',
        title_id: 'Entry & Exit',
        icon: ArrowLeftRight,
        color: 'amber',
        items: [
            { id: 'ee_1', text: 'Stop Loss set before entry', text_id: 'Stop Loss ditetapkan sebelum entry', isRule: true },
            { id: 'ee_2', text: 'Take Profit set before entry', text_id: 'Take Profit ditetapkan sebelum entry', isRule: true },
            { id: 'ee_3', text: 'No emotional SL adjustment', text_id: 'Tidak ubah SL ikut emosi', isRule: true }
        ]
    },
    risk_management: {
        title: 'Risk Management',
        title_id: 'Pengurusan Risiko',
        icon: ShieldCheck,
        color: 'primary',
        items: [
            { id: 'rm_1', text: 'Risk per trade:', text_id: 'Risiko per trade:', hasInput: true, placeholder: '1%', suffix: '%' },
            { id: 'rm_2', text: 'Maximum trades per day:', text_id: 'Maksimum trade sehari:', hasInput: true, placeholder: '3' },
            { id: 'rm_3', text: 'Maximum SL per day:', text_id: 'Maksimum SL sehari:', hasInput: true, placeholder: '3' },
            { id: 'rm_4', text: 'Lot size based on risk calculator', text_id: 'Saiz lot ikut risk calculator', isRule: true }
        ]
    },
    discipline: {
        title: 'Discipline',
        title_id: 'Disiplin',
        icon: Scale,
        color: 'cyan',
        items: [
            { id: 'dp_1', text: 'No revenge trading', text_id: 'Tidak revenge trade', isRule: true },
            { id: 'dp_2', text: 'No chasing price', text_id: 'Tidak kejar harga', isRule: true },
            { id: 'dp_3', text: 'Accept losses calmly', text_id: 'Terima loss dengan tenang', isRule: true }
        ]
    },
    stop_rule: {
        title: 'Stop Rule',
        title_id: 'Peraturan STOP',
        icon: OctagonX,
        color: 'rose',
        items: [
            { id: 'sr_1', text: 'Hit max loss → STOP', text_id: 'Capai max loss → STOP', isStop: true },
            { id: 'sr_2', text: 'Emotional disturbance → STOP', text_id: 'Emosi terganggu → STOP', isStop: true },
            { id: 'sr_3', text: 'Rule broken → STOP session', text_id: 'Langgar rule → STOP sesi', isStop: true }
        ]
    },
    after_trade: {
        title: 'After Trade',
        title_id: 'Selepas Trade',
        icon: ClipboardCheck,
        color: 'slate',
        items: [
            { id: 'at_1', text: 'Complete post-trade checklist', text_id: 'Lengkapkan checklist post-trade', isRule: true },
            { id: 'at_2', text: 'Fill simple trading journal', text_id: 'Isi trading journal ringkas', isRule: true }
        ]
    }
};

const colorStyles = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    primary: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' }
};

export const TradingPlan = ({ className }) => {
    const { language } = useLanguage();
    const planRef = useRef(null);
    
    const [formData, setFormData] = useState({
        ms_1: '',
        ms_2: '',
        rm_1: '1',
        rm_2: '3',
        rm_3: '3'
    });

    const [checkedRules, setCheckedRules] = useState({});

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRuleToggle = (ruleId) => {
        setCheckedRules(prev => ({
            ...prev,
            [ruleId]: !prev[ruleId]
        }));
    };

    const handleReset = () => {
        setFormData({
            ms_1: '',
            ms_2: '',
            rm_1: '1',
            rm_2: '3',
            rm_3: '3'
        });
        setCheckedRules({});
    };

    const handlePrint = () => {
        window.print();
    };

    // Count checked rules
    const totalRules = Object.values(tradingPlanSections).reduce((acc, section) => {
        return acc + section.items.filter(item => item.isRule || item.isStop).length;
    }, 0);
    const checkedCount = Object.values(checkedRules).filter(Boolean).length;

    return (
        <div className={cn("w-full max-w-3xl mx-auto", className)}>
            {/* Action Buttons - Hidden when printing */}
            <div className="flex items-center justify-between mb-6 print:hidden">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        className="rounded-xl"
                        data-testid="reset-plan"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                    <span className="text-sm text-slate-500">
                        {checkedCount}/{totalRules} {language === 'id' ? 'rules ditanda' : 'rules checked'}
                    </span>
                </div>
                <Button
                    onClick={handlePrint}
                    className="rounded-xl bg-primary hover:bg-primary/90"
                    data-testid="print-plan"
                >
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Cetak / PDF' : 'Print / PDF'}
                </Button>
            </div>

            {/* Trading Plan Content */}
            <div 
                ref={planRef}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden print:rounded-none print:border-0 print:shadow-none"
                data-testid="trading-plan"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-red-600 px-6 py-5 print:py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-heading font-bold text-white print:text-lg">
                                Trading Plan
                            </h1>
                            <p className="text-red-100 text-sm mt-1">
                                SEKOLAH NUSATRADERS
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-white/90 text-xs font-medium">
                                {language === 'id' ? 'Disiplin adalah kunci' : 'Discipline is key'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-5 print:p-4 print:space-y-3">
                    {Object.entries(tradingPlanSections).map(([sectionKey, section]) => {
                        const Icon = section.icon;
                        const colors = colorStyles[section.color];
                        const isStopSection = sectionKey === 'stop_rule';

                        return (
                            <section 
                                key={sectionKey}
                                className={cn(
                                    "rounded-xl border overflow-hidden print:break-inside-avoid",
                                    isStopSection ? "border-rose-200" : "border-slate-100"
                                )}
                                data-testid={`section-${sectionKey}`}
                            >
                                {/* Section Header */}
                                <div className={cn(
                                    "flex items-center gap-3 px-4 py-3 print:py-2",
                                    isStopSection ? "bg-rose-50" : "bg-slate-50"
                                )}>
                                    <div className={cn(
                                        "w-8 h-8 rounded-lg flex items-center justify-center print:w-6 print:h-6",
                                        colors.bg, colors.text
                                    )}>
                                        <Icon className="w-4 h-4 print:w-3 print:h-3" />
                                    </div>
                                    <h2 className={cn(
                                        "font-semibold text-sm uppercase tracking-wide",
                                        isStopSection ? "text-rose-700" : "text-slate-700"
                                    )}>
                                        {language === 'id' ? section.title_id : section.title}
                                    </h2>
                                </div>

                                {/* Section Items */}
                                <div className="px-4 py-3 space-y-2 print:py-2 print:space-y-1">
                                    {section.items.map((item) => {
                                        const isChecked = checkedRules[item.id] || false;

                                        // Input field item
                                        if (item.hasInput) {
                                            return (
                                                <div key={item.id} className="flex items-center gap-3">
                                                    <span className="text-sm text-slate-600 min-w-[140px] print:min-w-[120px] print:text-xs">
                                                        {language === 'id' ? item.text_id : item.text}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={formData[item.id] || ''}
                                                        onChange={(e) => handleInputChange(item.id, e.target.value)}
                                                        placeholder={item.placeholder}
                                                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary print:border-slate-300 print:py-1 print:text-xs"
                                                        data-testid={`input-${item.id}`}
                                                    />
                                                    {item.suffix && (
                                                        <span className="text-sm text-slate-500">{item.suffix}</span>
                                                    )}
                                                </div>
                                            );
                                        }

                                        // Select field item
                                        if (item.hasSelect) {
                                            return (
                                                <div key={item.id} className="flex items-center gap-3">
                                                    <span className="text-sm text-slate-600 min-w-[140px] print:min-w-[120px] print:text-xs">
                                                        {language === 'id' ? item.text_id : item.text}
                                                    </span>
                                                    <select
                                                        value={formData[item.id] || ''}
                                                        onChange={(e) => handleInputChange(item.id, e.target.value)}
                                                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white print:border-slate-300 print:py-1 print:text-xs"
                                                        data-testid={`select-${item.id}`}
                                                    >
                                                        <option value="">-</option>
                                                        {item.options.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            );
                                        }

                                        // Rule/checkbox item
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => handleRuleToggle(item.id)}
                                                className={cn(
                                                    "w-full flex items-center gap-3 py-1.5 text-left transition-colors rounded-lg px-2 -mx-2 print:py-1",
                                                    "hover:bg-slate-50 print:hover:bg-transparent",
                                                    isChecked && "bg-slate-50/50 print:bg-transparent"
                                                )}
                                                data-testid={`rule-${item.id}`}
                                            >
                                                {/* Checkbox */}
                                                <div className={cn(
                                                    "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all print:w-4 print:h-4",
                                                    isChecked
                                                        ? item.isStop
                                                            ? "bg-rose-500 border-rose-500 text-white"
                                                            : "bg-emerald-500 border-emerald-500 text-white"
                                                        : item.isStop
                                                            ? "border-rose-300"
                                                            : "border-slate-300"
                                                )}>
                                                    {isChecked && (
                                                        <svg className="w-3 h-3 print:w-2.5 print:h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>

                                                {/* Rule text */}
                                                <span className={cn(
                                                    "text-sm print:text-xs transition-colors",
                                                    isChecked && "text-slate-400 line-through",
                                                    !isChecked && item.isStop && "text-rose-700 font-medium",
                                                    !isChecked && item.highlight && "text-primary font-semibold",
                                                    !isChecked && !item.isStop && !item.highlight && "text-slate-700"
                                                )}>
                                                    {language === 'id' ? item.text_id : item.text}
                                                </span>

                                                {/* STOP badge */}
                                                {item.isStop && !isChecked && (
                                                    <span className="ml-auto text-[10px] font-bold text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full print:text-[8px]">
                                                        STOP
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                </div>

                {/* Reminder Section */}
                <div className="px-6 py-4 bg-amber-50 border-t border-amber-100 print:py-3">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 print:w-4 print:h-4" />
                        <div>
                            <p className="text-sm font-medium text-amber-800 print:text-xs">
                                {language === 'id'
                                    ? 'Jika ragu, jangan trade.'
                                    : "If you're unsure, don't trade."
                                }
                            </p>
                            <p className="text-sm text-amber-700 mt-0.5 print:text-xs">
                                {language === 'id'
                                    ? 'Lindungi modal dahulu. Profit datang kemudian.'
                                    : 'Protect capital first. Profit comes later.'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 print:py-2">
                    <p className="text-center text-xs text-slate-400">
                        {language === 'id'
                            ? 'Tandakan setiap rule sebagai peringatan sebelum trade'
                            : 'Check each rule as a reminder before trading'
                        }
                    </p>
                </div>
            </div>

            {/* Progress Indicator - Hidden when printing */}
            {checkedCount === totalRules && (
                <div className="mt-6 flex items-center justify-center gap-2 text-emerald-600 print:hidden" data-testid="plan-complete">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">
                        {language === 'id'
                            ? 'Semua rules ditanda. Anda sedia untuk trade!'
                            : 'All rules checked. You are ready to trade!'
                        }
                    </span>
                </div>
            )}

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    [data-testid="trading-plan"],
                    [data-testid="trading-plan"] * {
                        visibility: visible;
                    }
                    [data-testid="trading-plan"] {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .print\\:hidden {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};
